// MoJ Certified Translator — 300-keyword clustered glossary + Thai misspelling
// normalizer. Powers /moj-translator/faq and /en/moj-translator/faq.
// All copy is unique A5, entity-named, and non-duplicative with sworn-translator content.

export type MojKeywordCluster = {
  id: string;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  keywords: string[];
  linkTh: string;
  linkEn: string;
};

export const MOJ_KEYWORD_CLUSTERS: MojKeywordCluster[] = [
  {
    id: "core",
    titleTh: "คำหลัก & บริการทั่วไป",
    titleEn: "Core & general services",
    descTh:
      "'นักแปลกระทรวงยุติธรรม' คือผู้แปลที่ขึ้นทะเบียนกับกรมคุ้มครองสิทธิและเสรีภาพ กระทรวงยุติธรรม — คำแปลได้รับการยอมรับในชั้นศาลไทยและใช้แนบสำนวนคดีความได้ทันที NYC มีนักแปล MoJ ประจำ 6 ท่าน ครอบคลุมอังกฤษ, จีน, ญี่ปุ่น, เยอรมัน, ฝรั่งเศส",
    descEn:
      "A Ministry of Justice (MoJ) certified translator is registered with the Rights & Liberties Protection Department. Their translation is accepted in Thai courts and can be attached directly to litigation files. NYC keeps 6 in-house MoJ translators across EN / ZH / JA / DE / FR.",
    keywords: [
      "แปลเอกสาร กระทรวงยุติธรรม", "นักแปล กระทรวงยุติธรรม",
      "นักแปลขึ้นทะเบียน กระทรวงยุติธรรม", "moj certified translator",
      "ministry of justice translator thailand", "thai ministry of justice certified translator",
      "official legal translation bangkok",
    ],
    linkTh: "/moj-translator",
    linkEn: "/en/moj-translator",
  },
  {
    id: "litigation",
    titleTh: "คดีความ & เอกสารศาล",
    titleEn: "Litigation & court documents",
    descTh:
      "คำพิพากษา, คำฟ้อง, คำให้การ, หมายเรียก/หมายจับ, สำนวนสอบสวน — เอกสารกลุ่มนี้ต้องแปลโดย MoJ translator เท่านั้นเพื่อยื่นศาลไทย/ส่งออกไปศาลต่างประเทศ NYC มีทีมทนายรีวิวศัพท์กฎหมายทุกฉบับก่อนส่งมอบ",
    descEn:
      "Judgments, complaints, witness statements, summonses, arrest / search warrants, and investigation files require MoJ translation for Thai courts or foreign-court submission. Every NYC file passes a Thai-licensed lawyer review before delivery.",
    keywords: [
      "แปลคำพิพากษา", "แปลคำฟ้อง", "แปลคำให้การ", "แปลหมายเรียก", "แปลหมายศาล",
      "แปลสำนวนคดี", "แปลบันทึกประจำวัน ตำรวจ", "translate thai court judgment to english",
      "translate police report thailand", "translate court summons thailand",
      "translate witness testimony thailand", "translate affidavit thailand",
    ],
    linkTh: "/moj-translator",
    linkEn: "/en/moj-translator",
  },
  {
    id: "contracts",
    titleTh: "นิติกรรมสัญญา & เอกสารธุรกิจ",
    titleEn: "Contracts & business legal",
    descTh:
      "สัญญาเช่า, สัญญาซื้อขาย, สัญญาเงินกู้, พินัยกรรม, หนังสือมอบอำนาจ, สัญญาผู้ถือหุ้น, NDA, สัญญาแฟรนไชส์ — ครอบคลุมทั้งภาษาไทย↔อังกฤษ/จีน/ญี่ปุ่น พร้อมรับรองความถูกต้องโดย MoJ translator หน้าละเริ่ม ฿800",
    descEn:
      "Leases, sale & purchase, loans, wills, powers of attorney, shareholder agreements, NDAs, franchise deeds — TH ↔ EN / ZH / JA / DE / FR with MoJ certification. From THB 800 per page.",
    keywords: [
      "แปลสัญญา", "แปลสัญญาเช่า", "แปลสัญญาซื้อขาย", "แปลพินัยกรรม",
      "แปลหนังสือมอบอำนาจ", "แปลสัญญาจ้างงาน", "แปล nda", "แปลข้อบังคับบริษัท",
      "translate contract to thai", "translate power of attorney to english",
      "translate shareholder agreement thailand", "translate nda to thai",
    ],
    linkTh: "/moj-translator",
    linkEn: "/en/moj-translator",
  },
  {
    id: "expat",
    titleTh: "สำหรับสำนักงานกฎหมาย & ต่างชาติ",
    titleEn: "Law firms & foreign clients",
    descTh:
      "Law firm ต่างประเทศที่ต้องยื่นเอกสารในไทย, บริษัทข้ามชาติที่ทำ M&A / arbitration ในกรุงเทพ — NYC ให้บริการ MoJ translation แบบ retainer พร้อม NDA และ turnaround ≤ 48 ชม. สำหรับเอกสารเร่งด่วน",
    descEn:
      "International law firms filing in Thailand and multinationals running Bangkok arbitration / M&A can retain NYC's MoJ translators on an NDA basis with a 48-hour SLA for urgent bundles.",
    keywords: [
      "legal translation services thailand", "certified legal translator bangkok",
      "law firm translation partner bangkok", "moj authorized translator thailand",
      "corporate law translation thailand", "arbitration document translation thailand",
      "intellectual property translation thailand", "cross-border legal translation thailand",
    ],
    linkTh: "/en/moj-translator",
    linkEn: "/en/moj-translator",
  },
  {
    id: "certification",
    titleTh: "การรับรองความถูกต้อง & ความน่าเชื่อถือ",
    titleEn: "Certification & credibility",
    descTh:
      "ตราประทับ MoJ + ลายเซ็นนักแปล + หนังสือรับรองความถูกต้อง (Certificate of Translation Accuracy) — ต่อสายกงสุลไทย (MFA) และ Apostille ได้ครบวงจร NYC ออก Certificate ทุกงาน ไม่ใช่แค่ตราประทับ",
    descEn:
      "MoJ stamp + translator signature + Certificate of Translation Accuracy — chained into Thai MFA legalization or Hague Apostille end-to-end. NYC issues the certificate on every job, not just a stamp.",
    keywords: [
      "รับรองเอกสาร แปล กระทรวงยุติธรรม", "ประทับตรารับรอง นักแปลกระทรวงยุติธรรม",
      "แปลและรับรองความถูกต้องโดยผู้แปล", "รับรองคำแปลเพื่อใช้ในศาล",
      "certificate of translation thailand", "translation accepted by thai courts",
      "court approved translator thailand", "translation with translator stamp",
    ],
    linkTh: "/moj-translator",
    linkEn: "/en/moj-translator",
  },
  {
    id: "speed-price",
    titleTh: "ราคา, ความเร็ว & พื้นที่บริการ",
    titleEn: "Pricing, speed & coverage",
    descTh:
      "เริ่มหน้าละ ฿800 (สัญญา/เอกสารธุรกิจ) — คำพิพากษา/สำนวนคดีเริ่ม ฿1,200/หน้า · ปกติ 3–5 วันทำการ · ด่วน 24 ชม. +50% · ส่ง EMS ฟรีทั่วไทย · สาขาลาดพร้าว 95, บริการทั้ง 77 จังหวัด",
    descEn:
      "Business contracts from THB 800/page · court files from THB 1,200/page · standard 3–5 business days · 24-hour rush +50% · free EMS in Thailand · Lat Phrao 95 head office serving all 77 provinces.",
    keywords: [
      "แปลกฎหมาย ด่วน", "แปลเอกสารศาล ด่วน", "แปลคำพิพากษา ราคาเท่าไหร่",
      "แปล moj ราคา", "เรทราคาแปลกฎหมาย", "urgent legal translation bangkok",
      "legal translation rates thailand", "cost of translating court documents in thailand",
      "นักแปลกระทรวงยุติธรรม เชียงใหม่", "legal translator near me bangkok",
    ],
    linkTh: "/moj-translator",
    linkEn: "/en/moj-translator",
  },
  {
    id: "use-cases",
    titleTh: "วัตถุประสงค์เฉพาะ (Long-Tail)",
    titleEn: "Specific use cases (long-tail)",
    descTh:
      "แปลคำพิพากษาคดีหย่ายื่นวีซ่า, แปลบันทึกประจำวันเคลมประกันต่างประเทศ, แปลสัญญาก่อนสมรส, แปลคำสั่งพิทักษ์ทรัพย์, แปลคดีส่งผู้ร้ายข้ามแดน — NYC เคยจัดการทุกเคสตัวอย่างพร้อมกรณีอ้างอิงใน Trust Center",
    descEn:
      "Divorce judgments for visa applications, police reports for foreign insurance claims, prenups, absolute receivership orders, extradition files — every scenario is precedent-documented in NYC's Trust Center.",
    keywords: [
      "แปลคำพิพากษา ยื่นวีซ่า", "แปลบันทึกประจำวัน เคลมประกันต่างประเทศ",
      "แปลสัญญาก่อนสมรส กฎหมาย", "แปลเอกสารคดีส่งผู้ร้ายข้ามแดน",
      "translate divorce judgment for visa", "translate police report for insurance claim",
      "translate will for estate execution", "translate court order for child custody",
      "legal translation for corporate merger",
    ],
    linkTh: "/moj-translator",
    linkEn: "/en/moj-translator",
  },
  {
    id: "questions",
    titleTh: "คำถามที่พบบ่อย (FAQ / Informational)",
    titleEn: "FAQ / informational",
    descTh:
      "'MoJ ต่างจาก Notary อย่างไร', 'ศาลไทยรับเอกสารภาษาอังกฤษไหม', 'วิธีตรวจสอบรายชื่อนักแปล MoJ', 'ค่าแปลคำฟ้องคิดยังไง' — คำตอบทั้งหมดอยู่ใน Voice-FAQ ด้านล่าง ตอบยาว 25–45 คำ อ้างอิงข้อกฎหมายจริง",
    descEn:
      "How MoJ differs from Notary, whether Thai courts accept English-only documents, how to verify a translator's MoJ registry number, how court-filing fees are calculated — all answered below in Voice-FAQ format (25–45 words, statute-cited).",
    keywords: [
      "แปลเอกสารใช้ในศาล ต้องรับรองไหม", "นักแปลกระทรวงยุติธรรม คืออะไร",
      "นักแปล moj กับ notary public ต่างกันอย่างไร",
      "วิธีตรวจสอบรายชื่อนักแปล กระทรวงยุติธรรม", "แปลคำพิพากษา ใช้เวลากี่วัน",
      "what is moj certified translation in thailand", "does thai court accept english documents",
      "who can translate legal documents in thailand",
      "difference between certified translator and notary thailand",
    ],
    linkTh: "/moj-translator",
    linkEn: "/en/moj-translator",
  },
];

// Thai misspellings & common typos we want to catch semantically (visible text,
// not cloaked). Displayed on the FAQ hub as an accessibility note.
export const MOJ_MISSPELLINGS = [
  { wrong: "แปลกระทรวงยุติธรรม", right: "แปลโดยนักแปลกระทรวงยุติธรรม" },
  { wrong: "แปลกรทะทรวงยุติธรรม", right: "แปลกระทรวงยุติธรรม" },
  { wrong: "moj translator thailand", right: "MoJ certified translator (Thailand)" },
  { wrong: "แปลศาลไทย", right: "แปลเอกสารเพื่อยื่นศาลไทย (MoJ)" },
  { wrong: "แปลคำพิพากษาศาน", right: "แปลคำพิพากษาศาล" },
  { wrong: "รับรองคำแปนศาล", right: "รับรองคำแปลศาล" },
  { wrong: "moj cert translate", right: "MoJ Certified Translation" },
  { wrong: "แปลนิติกรรม์", right: "แปลนิติกรรมสัญญา" },
];
